import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { formatUSPhone, normalizePhoneForSave } from "../../utils/phoneFormat";
import SelectField from "../common/SelectField";
import { purchaseTicketSchema } from "../../../src/validators/validatorSchema";
import { validateData, clearError } from "../../../src/validators/YupValidations";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import EmailVerificationModal from "../auth/EmailVerificationModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentStatusModal from "../common/PaymentStatusModal";

const COUNTRY_CODE_OPTIONS = [
  { value: "+1", label: "+1" },
  { value: "+91", label: "+91" },
];

function PurchaseTicket({ event }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const ticketPrice = Number(event?.price) || 0;

  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    country_code: "+1",
  });

  const [errors, setErrors] = useState({});
  const [showGuestSection, setShowGuestSection] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [guestErrors, setGuestErrors] = useState([]);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'

  /* ---------------- PHONE HANDLING ---------------- */

  // const handleChange = (field, value) => {
  //   let updatedValue = value;

  //   if (field === "phone") {
  //     if (form.country_code === "+1") {
  //       const digits = normalizePhoneForSave(value).slice(0, 10);
  //       updatedValue = formatUSPhone(digits);
  //     } else {
  //       updatedValue = value.replace(/\D/g, "").slice(0, 10);
  //     }
  //   }

  //   setForm((prev) => ({ ...prev, [field]: updatedValue }));
  //   if (errors[field]) clearError(setErrors, field);
  // };

  const handleChange = (field, value) => {
    let updatedValue = value;

    if (field === "phone") {
      updatedValue = normalizePhoneForSave(value).slice(0, 10);
    }

    setForm((prev) => ({ ...prev, [field]: updatedValue }));

    if (field === "email" && isEmailVerified) {
      setIsEmailVerified(false);
    }

    if (errors[field]) clearError(setErrors, field);
  };

  const displayPhone = form.country_code === "+1" ? formatUSPhone(form.phone) : form.phone;

  const normalizedPhone = normalizePhoneForSave(form.phone);

  /* ---------------- GUEST LOGIC (SAME AS RESERVE) ---------------- */

  const openGuestSection = () => {
    if (!showGuestSection) {
      setShowGuestSection(true);
      setGuestList([""]);
    }
  };

  const addGuest = () => {
    // Prevent adding a new guest if the last one is empty
    if (guestList.length > 0 && guestList[guestList.length - 1].trim() === "") {
      // Set error for the last empty input
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[guestList.length - 1] = "Full name is required";
        return updated;
      });
      return;
    }
    setShowGuestSection(true);
    // add new guest input and focus it after render
    setGuestList((prev) => {
      const next = [...prev, ""];
      // focus the newly added input on next tick
      setTimeout(() => {
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs.length) {
          inputs[inputs.length - 1].focus();
        }
      }, 0);
      return next;
    });
    setGuestErrors((prev) => [...prev, ""]);
  };

  const removeGuest = (index) => {
    setGuestList((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setShowGuestSection(false);
      return updated;
    });
    setGuestErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuestChange = (value, index) => {
    setGuestList((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    // Clear error when user starts typing
    if (guestErrors[index]) {
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[index] = "";
        return updated;
      });
    }
  };

  const handleGuestKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If this is the last input, add a new guest (same behavior as + Add Guest)
      if (index === guestList.length - 1) {
        addGuest();
      } else {
        // move focus to next guest input if present
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs[index + 1]) inputs[index + 1].focus();
      }
    }
  };

  /* ---------------- CALCULATIONS ---------------- */

  const validPrimary =
    form.first_name.trim() &&
    form.last_name.trim() &&
    Yup.string().email().isValidSync(form.email) &&
    normalizedPhone.length === 10;

  const validGuests = guestList.filter((g) => g.trim() !== "");
  const totalTickets = (validPrimary ? 1 : 0) + validGuests.length;

  const totalAmount = useMemo(
    () => (totalTickets * ticketPrice).toFixed(2),
    [totalTickets, ticketPrice]
  );

  /* ---------------- SUBMIT ---------------- */

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   const isValid = await validateData(purchaseTicketSchema, form, setErrors);
  //   if (!isValid) return;

  //   const payload = {
  //     ...form,
  //     phone: normalizedPhone,
  //     guests: validGuests,
  //   };

  //   console.log("FINAL PAYLOAD", payload);
  //   console.log("TOTAL AMOUNT", totalAmount);
  // };

  const getErrorMessage = (err) => (typeof err === "string" ? err : err?.message || "");

  /* ---------------- UI ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    // if (!validate()) return;

    const isValid = await validateData(purchaseTicketSchema, form, setErrors);
    if (!isValid) return;

    if (!isEmailVerified) {
      toastErrorMessage("Please verify your email before proceeding.");
      return;
    }

    try {
      dispatch(SET_LOADING(true));

      const payload = {
        amount: Number(totalAmount),
        event_id: event._id,
        primary_included: true,
        first_name: form.first_name,
        middle_name: form.middle_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.country_code === "+1" ? formatUSPhone(form.phone || "") : form.phone || "",
        country_code: form.country_code,
        guests: guestList.filter((g) => g.trim() !== "").map((g) => ({ name: g })),
      };

      const response = await API.post(
        `${api_paths.member.payment.EVENT_CHECKOUT}?type=event&screen=event-detail`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data.payload.token;
        // Submit to Converge
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else if (response.data?.payload?.result?.success) {
        toastSuccessMessage("Ticket booked successfully!");
        dispatch(SET_LOADING(false));
        // Clear form?

        setGuestList([]);
        setShowGuestSection(false);
      }
    } catch (err) {
      console.error(err);
      toastErrorMessage(err?.response?.data?.message || "Booking failed");
      dispatch(SET_LOADING(false));
    }
  };

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/events/${id}`, { replace: true });
  };

  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate("/event-ticket", { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  return (
    <>
      <div className="ticket-card">
        <div className="ticket-card-header mb-4">
          <div className="px-4 pt-4 pb-2">
            <h1 className="ticket-card-title fw-semibold text-main f-20">Purchase Ticket</h1>
            <p className="ticket-card-subtitle mb-0 fs-14">
              Fill in your details to reserve your tickets
            </p>
          </div>
        </div>
        <form className="px-4 pb-3 purchase-form pt-3" onSubmit={handleSubmit} noValidate>
          <div className="row g-2">
            <div className="col-12 col-sm-12 col-md-4 col-lg-4 my-2 py-1 py-md-0 my-md-0">
              <input
                className="form-control"
                placeholder="First Name"
                value={form.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
              />
              {getErrorMessage(errors.first_name) && (
                <small className="text-danger">{getErrorMessage(errors.first_name)}</small>
              )}
            </div>
            <div className="col-12 col-sm-12 col-md-4 col-lg-4 my-2 py-1 py-md-0 my-md-0">
              <input
                className="form-control"
                placeholder="Middle Name"
                value={form.middle_name}
                onChange={(e) => handleChange("middle_name", e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-4 col-lg-4 my-2 py-1 py-md-0 my-md-0">
              <input
                className="form-control"
                placeholder="Last Name"
                value={form.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
              />
              {getErrorMessage(errors.last_name) && (
                <small className="text-danger">{getErrorMessage(errors.last_name)}</small>
              )}
            </div>
          </div>
          <div className="my-3 py-1">
            <div className="d-flex gap-2">
              <input
                className="form-control"
                placeholder="Email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                disabled={isEmailVerified}
              />
              <button
                type="button"
                // className={`btn ${isEmailVerified ? "btn-success" : "btn-secondary"}`}
                className={`btn ${isEmailVerified ? "btn-black" : "btn-orange"
                  }`}
                disabled={isEmailVerified || !form.email}
                onClick={() => setShowVerifyModal(true)}
              // style={{ minWidth: "100px" }}
              >
                {isEmailVerified ? "Verified" : "Verify"}
              </button>
            </div>
            {getErrorMessage(errors.email) && (
              <small className="text-danger">{getErrorMessage(errors.email)}</small>
            )}
          </div>
          {/* <div className="d-flex gap-2 mt-2">
          <div className="col-12 col-sm-12 col-md-4 col-lg-4 my-2 py-1 py-md-0 my-md-0">
            <input
              className="form-control"
              placeholder="Middle Name"
              value={form.middle_name}
              onChange={(e) => handleChange("middle_name", e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-12 col-md-4 col-lg-4 my-2 py-1 py-md-0 my-md-0">
            <input
              className="form-control"
              placeholder="Last Name"
              value={form.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
            />
            {getErrorMessage(errors.last_name) && (
              <small className="text-danger">
                {getErrorMessage(errors.last_name)}
              </small>
            )}
          </div>
        </div>
        <div className="my-3 py-1">
          <div className="d-flex gap-2">
            <input
              className="form-control"
              placeholder="Email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              disabled={isEmailVerified}
            />
            <button
              type="button"
              // className={`btn ${isEmailVerified ? "btn-success" : "btn-secondary"}`}
               className={`btn ${isEmailVerified ? "btn-black" : "btn-orange"
                            }`}
              disabled={isEmailVerified || !form.email}
              onClick={() => setShowVerifyModal(true)}
            >
              {isEmailVerified ? "Verified" : "Verify"}
            </button>
          </div>
          {getErrorMessage(errors.email) && (
            <small className="text-danger">
              {getErrorMessage(errors.email)}
            </small>
          )}
        </div>
        {/* <div className="d-flex gap-2 mt-2">
          <div style={{ width: 100 }}>
            <SelectField
              options={COUNTRY_CODE_OPTIONS}
              value={COUNTRY_CODE_OPTIONS.find(
                (o) => o.value === form.country_code
              )}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  country_code: opt.value,
                  phone: "",
                }))
              }
            />
          </div>

          <input
            className={`form-control ${errors.phone ? "is-invalid" : ""}`}
            placeholder="Phone Number"
            value={form.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            maxLength={form.country_code === "+1" ? 15 : 10}
          />
        </div> */}
          <div className="row g-2 align-items-start">
            <div className="col-4 col-sm-4 col-md-4 col-lg-4">
              <SelectField
                className="px-0"
                options={COUNTRY_CODE_OPTIONS}
                value={COUNTRY_CODE_OPTIONS.find((o) => o.value === form.country_code)}
                onChange={(opt) =>
                  setForm((prev) => ({
                    ...prev,
                    country_code: opt.value,
                    phone: "",
                  }))
                }
              />
            </div>

            <div className="col-8 col-sm-8 col-md-8 col-lg-8">
              <input
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                placeholder="Phone Number"
                value={displayPhone}
                onChange={(e) => handleChange("phone", e.target.value)}
                maxLength={form.country_code === "+1" ? 15 : 10}
              />
            </div>
          </div>

          {getErrorMessage(errors.phone) && (
            <small className="text-danger">{getErrorMessage(errors.phone)}</small>
          )}

          {/* -------- ADD GUEST (SAME UI AS RESERVE) -------- */}

          <div style={{ marginTop: 18 }}>
            {/* <label
            onClick={openGuestSection}
            style={{
              display: "block",
              cursor: "pointer",
              fontWeight: 600,
              color: "#E69F00",
              marginBottom: 12,
            }}
          >
            Add Guest Tickets
          </label> */}
            {/* <label
              onClick={openGuestSection}
              style={{
                display: "block",
                cursor: "pointer",
                fontWeight: 600,
                color: "#E69F00", // label color
                paddingBottom: 8,
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div className="icon-plus me-2">+</div>
              <div className="mt-1"> Add Guests Tickets</div>
            </label> */}
            <label
              className="btn btn-orange d-flex flex-row mb-3"
              onClick={openGuestSection}
              style={{ width: "fit-content" }}
            >
              {/* <div className="icon-plus me-2">+</div> */}
              <div> Add Guests</div>
            </label>
            <div>
              <p className="mb-4 f-16 fw-semibold orange">Note: Children under 5 years enter free.</p>
            </div>
            {showGuestSection && (
              <>
                {guestList.map((g, i) => (
                  // <div
                  //   key={i}
                  //   className="guest-row"
                  //   style={{
                  //     display: "flex",
                  //     alignItems: "center",
                  //     marginBottom: 10,
                  //   }}
                  // >
                  //   <div style={{ flex: 1 }}>
                  //     <input
                  //       className="input"
                  //       placeholder="Full Name"
                  //       value={g}
                  //       onChange={(e) => handleGuestChange(e.target.value, i)}
                  //       onKeyDown={(e) => handleGuestKeyDown(e, i)}
                  //       style={{
                  //         width: "100%",
                  //         padding: "10px",
                  //         borderRadius: 6,
                  //         border: guestErrors[i] ? "1px solid #dc3545" : "1px solid #ccc",
                  //       }}
                  //     />
                  //     {guestErrors[i] && (
                  //       <small
                  //         style={{ color: "#dc3545", fontSize: 12, marginTop: 4, display: "block" }}
                  //       >
                  //         {guestErrors[i]}
                  //       </small>
                  //     )}
                  //   </div>

                  //   <button
                  //     type="button"
                  //     onClick={() => removeGuest(i)}
                  //     style={{
                  //       marginLeft: 8,
                  //       background: "transparent",
                  //       border: "none",
                  //       color: "#c23",
                  //       fontSize: 18,
                  //       cursor: "pointer",
                  //     }}
                  //   >
                  //     ✕
                  //   </button>
                  // </div>
                  <div className="guest-row" key={i}>
                    <div className="guest-input-wrapper w-100 mb-2">
                      <input
                        className="form-control"
                        placeholder="Full Name"
                        value={g}
                        onChange={(e) => handleGuestChange(e.target.value, i)}
                        onKeyDown={(e) => handleGuestKeyDown(e, i)}
                        style={{
                          border: guestErrors[i]
                            ? "1px solid #dc3545"
                            : "1px solid var(--border-color)",
                        }}
                      />

                      <button
                        type="button"
                        className="guest-remove-btn"
                        onClick={() => removeGuest(i)}
                        aria-label="remove guest"
                      >
                        ✕
                      </button>

                      {guestErrors[i] && (
                        <small className="text-danger d-block mt-1">{guestErrors[i]}</small>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addGuest}
                  style={{
                    width: "100%",
                    background: "var(--border-color)",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: 500,
                    marginBottom: "15px",
                    cursor: "pointer",
                    color: "var(--text-main)",
                    fontSize: "14px",
                  }}
                >
                  + Add Guest
                </button>
              </>
            )}
          </div>

          {/* -------- SUMMARY -------- */}
          <div className="border-hr">
            <div className="amount-row mt-3">
              <span>Total Tickets</span>
              <strong>{totalTickets}</strong>
            </div>

            <div className="total-amount fw-semibold d-flex justify-content-between">
              <p className="fw-semibold">Total Amount</p>
              <span className="fw-semibold">${totalAmount}</span>
            </div>
          </div>
          <button
            className="btn btn-orange-md w-100 my-2"
            type="submit"
            disabled={totalTickets === 0}
          >
            RSVP & Purchase Tickets
          </button>
        </form>

        <EmailVerificationModal
          show={showVerifyModal}
          onHide={() => setShowVerifyModal(false)}
          email={form.email}
          onVerified={() => setIsEmailVerified(true)}
        />
      </div>

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
          buttonName={"event"}
          type={"event"}
          screen={"purchase"}
        />
      )}
    </>
  );
}

export default PurchaseTicket;
